import React, { useEffect } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
  ReferenceLine,
} from 'recharts';
import clsx from 'clsx';

import { toPercent, numberFormatter, under2camel } from 'lib/commonLib';

import { makeStyles } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core/styles';

import ChartWrap from './ChartWrap';
import ChartTooltip from './ChartTooltip';

import LineIcon from 'images/common/chart/line';
import RectIcon from 'images/common/chart/rect';

const legendStyles = makeStyles((theme) => ({
  chartWrap: { boxSizing: 'border-box' },
  legendUl: {
    width: '75%',
    margin: '0 auto',
    listStyle: 'none',
    textAlign: 'center',
  },
  li: {
    display: 'inline-block',
    marginRight: '10px',
    cursor: 'pointer',
    '&.invisible': { opacity: 0.5 },
  },
}));

// 임시 나중에 합쳐야함
const renderTooltipContent = ({
  isPercent,
  payload,
  label,
  keyLabel,
  customizeData,
  dataKeys,
  makeLabel,
}) => {
  if (typeof makeLabel !== 'undefined') {
    label = makeLabel(label);
  }

  return (
    <ChartTooltip
      label={label}
      payload={payload}
      keyLabel={keyLabel}
      isPercent={isPercent}
      customizeData={customizeData}
      dataKeys={dataKeys}
    />
  );
};

/**
 * y 축 만들어 주는 함수
 * @param {json} param0 { axisData: 축데이터, orientation: 'right' || 'left' }
 */
const makeYAxis = ({
  axisData,
  orientation,
  keyLabel,
  maxData = {},
  customizeData,
  visible,
}) => {
  const result = [];
  let textLength;
  let hideFlag = false;

  if (axisData.individually === true) {
    for (let key in axisData) {
      if (axisData.hasOwnProperty(key) && key !== 'individually') {
        const { isPercent } = axisData[key];
        if (typeof keyLabel[key] !== 'undefined') {
          textLength = keyLabel[key].length * 13;
        } else {
          textLength = 0;
        }
        if (!!customizeData[key]) {
          textLength = customizeData[key].width;
        }
        result.push(
          <YAxis
            hide={hideFlag ? false : !visible[key]}
            key={key}
            yAxisId={key}
            orientation={orientation}
            tickFormatter={Boolean(isPercent) ? toPercent : numberFormatter}
            width={textLength}
            label={
              typeof keyLabel[key] !== 'undefined' && {
                value: hideFlag ? '' : `(${keyLabel[key]})`,
                position: 'top',
                offset: 15,
                fill: '#666',
              }
            }
            domain={
              maxData[key] !== undefined
                ? ['auto', isPercent ? 'auto' : maxData[key]]
                : [0, 'auto']
            }
          />
        );
      }
    }
  } else {
    let key = null;
    let isPercent = false;
    for (let curKey in axisData) {
      if (axisData.hasOwnProperty(curKey)) {
        if (curKey === 'isPercent') {
          isPercent = axisData['isPercent'];
        } else {
          key = curKey;
        }
      }
    }
    if (typeof keyLabel[key] !== 'undefined') {
      textLength = keyLabel[key].length;
    } else {
      textLength = 0;
    }

    if (typeof customizeData[key] !== 'undefined') {
      hideFlag = !!customizeData[key].required
        ? customizeData[key].required
        : false;
    }

    result.push(
      <YAxis
        hide={hideFlag ? false : !visible[key]}
        key={`yAxis_${orientation}`}
        yAxisId={orientation}
        orientation={orientation}
        tickFormatter={Boolean(isPercent) ? toPercent : numberFormatter}
        domain={
          maxData[key] !== undefined
            ? ['auto', isPercent ? 'auto' : maxData[key]]
            : [0, 'auto']
        }
        label={
          orientation === 'left'
            ? textLength > 13
              ? typeof keyLabel[key] !== 'undefined' && {
                  value: hideFlag ? '' : `(${keyLabel[key]})`,
                  position: 'insideBottomLeft',
                  offset: -60,
                  fill: '#666',
                  angle: 90,
                }
              : typeof keyLabel[key] !== 'undefined' && {
                  value: hideFlag ? '' : `(${keyLabel[key]})`,
                  position: 'top',
                  offset: 15,
                  fill: '#666',
                }
            : orientation === 'right' && textLength > 12
            ? typeof keyLabel[key] !== 'undefined' && {
                value: hideFlag ? '' : `(${keyLabel[key]})`,
                position: 'insideBottomRight',
                offset: -15,
                fill: '#666',
                angle: 90,
              }
            : typeof keyLabel[key] !== 'undefined' && {
                value: hideFlag ? '' : `(${keyLabel[key]})`,
                position: 'top',
                offset: 15,
                fill: '#666',
              }
        }
      />
    );
  }
  return result;
};

/**
 * 축 id 반환
 * @param {json} param0 { axisData: 축데이터, key: dataKey }
 */
const getYAxisId = ({ axisData, key }) => {
  let yAxisId = 'left';
  if (typeof axisData !== 'undefined') {
    if (
      axisData.left.individually === true &&
      typeof axisData.left[key] !== 'undefined'
    ) {
      yAxisId = key;
    } else if (typeof axisData.right[key] !== 'undefined') {
      if (axisData.right.individually === true) {
        yAxisId = key;
      } else {
        yAxisId = 'right';
      }
    }
  }
  return yAxisId;
};

/**
 * legend 컴포넌트
 * @param {json} props
 */
const CustomizedLegend = (props) => {
  const { payload, keyLabel = {}, handleOnClick, visible } = props;
  const classes = legendStyles();
  const [legendList /*, setLegendList*/] = React.useState(
    (() => {
      const result = [];
      // 처음에 legend list 셋팅해줌
      for (let i = 0; i < payload.length; i++) {
        const { dataKey } = payload[i];
        result.push({
          name:
            typeof keyLabel[dataKey] === 'undefined'
              ? dataKey
              : keyLabel[dataKey],
          ...payload[i],
        });
      }

      return result;
    })()
  );

  return (
    <ul className={classes.legendUl}>
      {(() => {
        if (legendList.length > 1) {
          return legendList.map((entry, index) => {
            const { value, name, type, color } = entry;
            return (
              <li
                key={`item-${index}`}
                className={clsx(classes.li, { invisible: !visible[value] })}
                onClick={() => handleOnClick(value)}
              >
                {type === 'line' ? (
                  <LineIcon fill={color} />
                ) : (
                  <RectIcon fill={color} />
                )}
                {name}
              </li>
            );
          });
        } else {
        }
      })()}
    </ul>
  );
};

export default function MultiplexChart({
  xInterval = 'preserveStartEnd',
  xAxisTick,
  makeLabel,
  className = '',
  width = '100%',
  height = 300,
  title,
  isPercent,
  data,
  dataKeys,
  legend = false,
  variousColoredCells = false,
  legendData,
  keyLabel,
  customizeData = {},
  barLabel = false,
  stackId,
  yAxisData,
  maxData,
  brush = false,
  referenceLine,
  useTitle,
  hideKey = [],
}) {
  const theme = useTheme();
  const classes = legendStyles();

  const { graph } = theme.palette;
  const [visible, setVisible] = React.useState(
    (() => {
      const result = {};
      for (let item in dataKeys) {
        if (dataKeys.hasOwnProperty(item)) {
          const curChartData = dataKeys[item];
          for (let i = 0; i < curChartData.length; i++) {
            const curKey = curChartData[i];
            result[curKey] = true;
          }
        }
      }
      return result;
    })()
  );

  useEffect(() => {
    let newVisible = { ...visible };
    if (!!hideKey) {
      hideKey.forEach((e) => {
        if (!!visible[e]) {
          newVisible[e] = false;
        }
      });
    }
    if (JSON.stringify(visible) !== JSON.stringify(newVisible)) {
      setVisible(newVisible);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setVisible(
      (() => {
        const result = {};
        for (let item in dataKeys) {
          if (dataKeys.hasOwnProperty(item)) {
            const curChartData = dataKeys[item];
            for (let i = 0; i < curChartData.length; i++) {
              const curKey = curChartData[i];
              result[curKey] = true;
            }
          }
        }
        return result;
      })()
    );
    // eslint-disable-next-line
  }, [data]);

  const makeTootipContent = (props) => {
    return renderTooltipContent({
      ...props,
      keyLabel,
      isPercent,
      customizeData,
      dataKeys,
      makeLabel,
    });
  };

  const handleHiddenData = (value) => {
    setVisible({
      ...visible,
      [value]: !visible[value],
    });
  };

  const colorCondition = (key, idx) => {
    let newColor = '';
    switch (key) {
      case 'ADVRTS_AMT':
        newColor = theme.palette.common.red;
        break;
      case 'BROWSER_SESSION_ROAS':
        newColor = theme.palette.table.cell.purple;
        break;
      case 'MOB_SESSION_ROAS':
        newColor = theme.palette.table.cell.purple;
        break;
      case 'SESSION_ROAS':
        newColor = theme.palette.table.cell.purple;
        break;
      case 'DIRECT_ROAS':
        newColor = theme.palette.table.cell.purple;
        break;
      case 'ROAS':
        newColor = theme.palette.table.cell.purple;
        break;
      case 'CLICK_RATE':
        newColor = theme.palette.table.cell.blue;
        break;
      case 'ECPM':
        newColor = theme.palette.table.cell.green;
        break;
      default:
        newColor = graph.color[idx % maxIndex];
    }
    return newColor;
  };

  const CustomizedLabel = (props) => {
    const theme = useTheme();
    const { viewBox, label, keyLabel } = props;
    const tempStyle = {
      fill: '' + theme.palette.table.cell.red,
    };
    if (!!referenceLine[keyLabel]['color']) {
      tempStyle['fill'] = referenceLine[keyLabel]['color'];
    }
    return (
      <text {...viewBox} textAnchor="middle">
        <tspan x={viewBox.width} dy="18" style={tempStyle}>
          {label}
        </tspan>
      </text>
    );
  };

  const legendHeight = 35;
  const tickFormatter = isPercent ? toPercent : numberFormatter;
  const maxIndex = Object.keys(graph.color).length;
  let colorIndex = 0;

  const haveRightYAxis =
    typeof yAxisData !== 'undefined' &&
    Object.keys(yAxisData.right).length !== 0;
  const { bar, line } = dataKeys;
  return (
    <ChartWrap
      className={`${className} ${classes.chartWrap}`}
      key={title}
      title={typeof useTitle !== 'undefined' ? title : null}
    >
      <div style={{ width, height }}>
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            margin={{
              top: 5,
              right:
                typeof yAxisData !== 'undefined' && !haveRightYAxis ? 0 : 30,
              left: 40,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" xAxisId={0} hide />
            <XAxis
              dataKey="name"
              xAxisId={1}
              tick={xAxisTick}
              interval={xInterval}
            />
            {typeof yAxisData !== 'undefined' ? (
              (() => {
                const result = [
                  ...makeYAxis({
                    axisData: yAxisData.left,
                    orientation: 'left',
                    keyLabel,
                    maxData,
                    customizeData,
                    visible,
                  }),
                ];

                if (haveRightYAxis) {
                  result.push(
                    ...makeYAxis({
                      axisData: yAxisData.right,
                      orientation: 'right',
                      keyLabel,
                      maxData,
                      customizeData,
                      visible,
                    })
                  );
                }
                return result;
              })()
            ) : (
              <YAxis
                yAxisId="left"
                orientation="left"
                tickFormatter={tickFormatter}
                domain={isPercent ? [0, 100] : [0, 'auto']}
              />
            )}
            <Tooltip
              content={makeTootipContent}
              wrapperStyle={{ zIndex: 999 }}
            />

            {legend &&
              (typeof legendData === 'undefined' ? (
                <Legend
                  onClick={handleHiddenData}
                  verticalAlign="top"
                  height={legendHeight}
                  content={
                    <CustomizedLegend
                      keyLabel={keyLabel}
                      handleOnClick={handleHiddenData}
                      visible={visible}
                      hideKey={hideKey}
                    />
                  }
                  wrapperStyle={{ top: '-5px' }}
                />
              ) : (
                <Legend
                  height={legendHeight}
                  verticalAlign="bottom"
                  align="right"
                  content={legendData}
                />
              ))}
            {Boolean(bar) &&
              bar.map((key, idx) => {
                let yAxisId = getYAxisId({ axisData: yAxisData, key });
                let result = null;
                let newStackId = stackId;
                let xAxisId = 0;

                let color = graph.color[0];
                if (!!customizeData[key]) {
                  if (!!customizeData[key]['color']) {
                    color = customizeData[key]['color'];
                  } else {
                    color = colorCondition(key, idx);
                  }
                  if (!!customizeData[key]['stackId']) {
                    newStackId = customizeData[key][stackId];
                  }
                  if (!!customizeData[key]['xAxisId']) {
                    xAxisId = customizeData[key]['xAxisId'];
                  }
                } else {
                  color = colorCondition(key, idx);
                }
                if (typeof newStackId === 'object') {
                  newStackId = stackId.join('$');
                }
                result = (
                  <Bar
                    hide={!visible[key]}
                    yAxisId={yAxisId}
                    xAxisId={xAxisId}
                    stackId={newStackId}
                    key={idx}
                    dataKey={key}
                    fill={color}
                    label={barLabel ? { position: 'top' } : null}
                  >
                    {data.map((d, index) =>
                      variousColoredCells ? (
                        <Cell
                          key={`cell-${key}`}
                          fill={graph.color[colorIndex++ % maxIndex]}
                        />
                      ) : (
                        <Cell key={`cell-${key}`} />
                      )
                    )}
                  </Bar>
                );

                // if (
                //   typeof visible[key] !== 'undefined' &&
                //    === false
                // ) {
                //   result = null;
                // }

                return result;
              })}
            {Boolean(line) &&
              line.map((key) => {
                let stackColor = graph.color[colorIndex++ % maxIndex];
                const yAxisId = getYAxisId({ axisData: yAxisData, key });
                let result = null;
                if (!!customizeData[key]) {
                  stackColor = customizeData[key].color;
                }

                result = (
                  <Line
                    hide={!visible[key]}
                    key={key}
                    yAxisId={yAxisId}
                    type="monotone"
                    dataKey={key}
                    stroke={stackColor}
                    strokeWidth={2}
                    activeDot={{ strokeWidth: 2 }}
                  />
                );

                // if (
                //   typeof visible[key] !== 'undefined' &&
                //   visible[key] === false
                // ) {
                //   result = null;
                // }

                return result;
              })}
            {typeof referenceLine !== 'undefined' &&
              // 평균선 한개로 임의 정의 여러개 추가된다거나 max 등은 추후 개선
              (() => {
                const reulst = [];
                let lineWidth = 1;
                if (!!customizeData['referenceLine']) {
                  lineWidth = !!customizeData['referenceLine'].width
                    ? customizeData['referenceLine'].width
                    : 1;
                }
                for (let key in referenceLine) {
                  let camelKey = under2camel(key);
                  const yAxisId = getYAxisId({ axisData: yAxisData, key });
                  reulst.push(
                    <ReferenceLine
                      key={key + 'refLine'}
                      y={referenceLine[key].y}
                      label={
                        <CustomizedLabel
                          label={referenceLine[key].label}
                          keyLabel={key}
                        />
                      }
                      yAxisId={yAxisId}
                      stroke={
                        typeof referenceLine[key].color !== 'undefined'
                          ? referenceLine[key].color
                          : theme.palette.table.cell[camelKey]
                      }
                      strokeWidth={lineWidth}
                    />
                  );
                }
                return reulst;
              })()}
            {brush !== false && (
              <Brush
                dataKey="name"
                height={30}
                stroke="#8884d8"
                startIndex={brush.startIndex}
                endIndex={brush.endIndex}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </ChartWrap>
  );
}
